// המיניסייט panel (mock s4, left rail) — snapshot of the contact's curated
// favorite-listings minisite: viewed / not-viewed per listing (reactions +
// visited arrays on the favorites doc + listing-page-views counters), share
// controls reusing the /myListings/:favDocId share-link convention, and a
// jump to the matches tab. Live: onSnapshot on the (user-scoped) favorites
// doc, mirrored into sessionCache so re-opens render instantly.
import { useEffect } from "react";
import {
  Box,
  Button,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography
} from "@material-ui/core";
import { WhatsApp, FavoriteBorder } from "@material-ui/icons";
import { alpha, useTheme } from "@material-ui/core/styles";
import { db } from "firebaseConfig";
import Label from "components/Label";
import CopyClipboard from "components/CopyClipboard";
import useAuth from "hooks/useAuth";
import { PATH_PUBLIC } from "routes/paths";
import { GenericConverter } from "utils/firestoreConverters";
import { setCached, useSessionCacheValue } from "../../data/sessionCache";
import { OverviewPanel } from "./OverviewPanel";
import { useSwitchTab } from "./useSwitchTab";
import {
  favoritesCacheKey,
  fetchFavoritesDocOnce,
  pageViewsCacheKey,
  fetchPageViewsDoc,
  minisiteListingsCacheKey,
  fetchMinisiteListings,
  listingAddressText,
  FAVORITES_MAX_AGE_MS,
  PAGE_VIEWS_MAX_AGE_MS
} from "./overviewData";
import { CONTACT_FAVORITE_LISTINGS_COLLECTION_NAME } from "../../../../../../@types/sharedSchema";
import type {
  RealEstateContact,
  FavoriteListingsOfContact,
  ListingPageViewsDocument
} from "../../../../../../@types/sharedSchema";
import type { FirestoreListingHit } from "utils/listings/fetchListings";

type OverviewMinisitePanelProps = {
  contact: RealEstateContact;
};

const MAX_VISIBLE_LISTINGS = 3;

function ListingThumb({ listing }: { listing: FirestoreListingHit | undefined }): JSX.Element {
  const theme = useTheme();
  const imageUrl = listing?.coverImageUrl || listing?.imageUrls?.[0];
  return (
    <Box
      sx={{
        width: 44,
        height: 44,
        flexShrink: 0,
        borderRadius: 1,
        bgcolor: alpha(theme.palette.grey[500], 0.16),
        backgroundImage: imageUrl ? `url(${imageUrl})` : undefined,
        backgroundSize: "cover",
        backgroundPosition: "center"
      }}
    />
  );
}

// Inner content once the favorites doc exists — hook keys stay stable because
// this component only mounts with a resolved doc.
function MinisiteContent({
  contact,
  favoritesDoc
}: {
  contact: RealEstateContact;
  favoritesDoc: FavoriteListingsOfContact & { id?: string };
}): JSX.Element {
  const switchTab = useSwitchTab();

  const favoritesDocId = favoritesDoc.id ?? "";
  const listingIds = favoritesDoc.listingIds ?? [];
  const liked = new Set(favoritesDoc.listingIdsUserLiked ?? []);
  const disliked = new Set(favoritesDoc.listingIdsUserDisliked ?? []);
  const visited = new Set(favoritesDoc.listingIdsUserVisited ?? []);

  const { data: pageViews } = useSessionCacheValue<ListingPageViewsDocument | null>(
    pageViewsCacheKey(favoritesDocId),
    () => fetchPageViewsDoc(favoritesDocId),
    { maxAgeMs: PAGE_VIEWS_MAX_AGE_MS }
  );
  const viewCounts = pageViews?.listingDetailViewCounts ?? {};

  const isViewed = (listingId: string): boolean =>
    visited.has(listingId) || (viewCounts[listingId] ?? 0) > 0;

  // Most engaging first: liked → viewed → the rest (mirrors the mock ordering).
  const sortedIds = [...listingIds].sort((a, b) => {
    const rank = (id: string): number => (liked.has(id) ? 0 : isViewed(id) ? 1 : 2);
    return rank(a) - rank(b);
  });
  const visibleIds = sortedIds.slice(0, MAX_VISIBLE_LISTINGS);

  const { data: listings, loading: listingsLoading } = useSessionCacheValue<FirestoreListingHit[]>(
    minisiteListingsCacheKey(visibleIds),
    () => fetchMinisiteListings(visibleIds)
  );
  const listingById = new Map((listings ?? []).map((listing) => [listing.id ?? "", listing]));

  const shareLink = `${window.location.origin}${PATH_PUBLIC.myListings}/${favoritesDocId}`;
  const waDigits = (contact.id || "").replace(/\D/g, "");
  const whatsAppShareLink = waDigits
    ? `https://api.whatsapp.com/send?phone=${waDigits}&text=${encodeURIComponent(shareLink)}`
    : `https://api.whatsapp.com/send?text=${encodeURIComponent(shareLink)}`;

  if (!listingIds.length) {
    return (
      <Stack sx={{ gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          עדיין לא נוספו נכסים למיניסייט של הלקוח.
        </Typography>
        <Button
          size="small"
          variant="text"
          onClick={() => switchTab("matches")}
          sx={{ alignSelf: "flex-start" }}
        >
          לניהול הנכסים ←
        </Button>
      </Stack>
    );
  }

  return (
    <Stack sx={{ gap: 1 }}>
      {visibleIds.map((listingId) => {
        const listing = listingById.get(listingId);
        const views = viewCounts[listingId] ?? 0;
        return (
          <Stack key={listingId} direction="row" alignItems="center" sx={{ gap: 1.25 }}>
            <ListingThumb listing={listing} />
            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              {listing ? (
                <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap dir="auto">
                  {listingAddressText(listing)}
                </Typography>
              ) : listingsLoading ? (
                <Skeleton variant="text" width={120} />
              ) : (
                <Typography variant="body2" color="text.secondary" noWrap>
                  נכס שהוסר
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" noWrap>
                {isViewed(listingId)
                  ? views > 0
                    ? `נצפה ×${views}`
                    : "נצפה"
                  : "נשלח ללקוח"}
                {disliked.has(listingId) ? " · לא התאים" : ""}
              </Typography>
            </Box>
            {liked.has(listingId) ? (
              <Tooltip title="הלקוח אהב את הנכס">
                <FavoriteBorder sx={{ fontSize: 18, color: "error.main" }} />
              </Tooltip>
            ) : !isViewed(listingId) ? (
              <Label color="warning" variant="ghost">
                טרם נצפה
              </Label>
            ) : null}
          </Stack>
        );
      })}

      {listingIds.length > MAX_VISIBLE_LISTINGS && (
        <Typography variant="caption" color="text.secondary">
          ועוד {listingIds.length - MAX_VISIBLE_LISTINGS} נכסים במיניסייט
        </Typography>
      )}

      <Stack direction="row" alignItems="center" sx={{ gap: 0.5, mt: 0.5 }}>
        <CopyClipboard value={shareLink} iconSize={18} />
        <Tooltip title="שיתוף בוואטסאפ">
          <IconButton
            size="small"
            href={whatsAppShareLink}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: "#25D366" }}
            aria-label="שיתוף בוואטסאפ"
          >
            <WhatsApp sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" variant="text" onClick={() => switchTab("matches")}>
          לניהול הנכסים ←
        </Button>
      </Stack>
    </Stack>
  );
}

export function OverviewMinisitePanel({ contact }: OverviewMinisitePanelProps): JSX.Element {
  const switchTab = useSwitchTab();
  const { user } = useAuth();
  const userEmail: string = user?.email ?? "";

  // undefined == not fetched yet (skeleton); null == no minisite for this contact.
  const { data: favoritesDoc, error } = useSessionCacheValue<FavoriteListingsOfContact | null>(
    favoritesCacheKey(contact.id),
    () => fetchFavoritesDocOnce(contact.id),
    { maxAgeMs: FAVORITES_MAX_AGE_MS }
  );

  // Live upgrade: keep the cached favorites doc fresh while the page is open
  // (customer reactions land in realtime). User-scoped query — client
  // Firestore is the sanctioned path here.
  useEffect(() => {
    if (!contact.id || !userEmail) return undefined;
    const unsubscribe = db
      .collection(CONTACT_FAVORITE_LISTINGS_COLLECTION_NAME)
      .where("realtorId", "==", userEmail)
      .where("contactId", "==", contact.id)
      .limit(1)
      .withConverter(GenericConverter<FavoriteListingsOfContact>())
      .onSnapshot(
        (snapshot) => {
          setCached<FavoriteListingsOfContact | null>(
            favoritesCacheKey(contact.id),
            snapshot.empty ? null : snapshot.docs[0].data()
          );
        },
        (error) => {
          console.error("OverviewMinisitePanel: favorites listener failed", error);
        }
      );
    return unsubscribe;
  }, [contact.id, userEmail]);

  const firstName = contact.callerFirstName?.trim();
  const title = firstName ? `המיניסייט של ${firstName}` : "המיניסייט של הלקוח";

  const viewedBadge = (() => {
    if (!favoritesDoc?.listingIds?.length) return undefined;
    const visited = new Set(favoritesDoc.listingIdsUserVisited ?? []);
    const viewed = favoritesDoc.listingIds.filter((id) => visited.has(id)).length;
    return (
      <Label color="info" variant="ghost">
        {viewed}/{favoritesDoc.listingIds.length} נצפו
      </Label>
    );
  })();

  return (
    <OverviewPanel title={title} headerEnd={viewedBadge}>
      {favoritesDoc ? (
        <MinisiteContent contact={contact} favoritesDoc={favoritesDoc} />
      ) : favoritesDoc === undefined && !error ? (
        <Stack sx={{ gap: 1 }}>
          {[0, 1].map((row) => (
            <Stack key={row} direction="row" alignItems="center" sx={{ gap: 1.25 }}>
              <Skeleton variant="rectangular" width={44} height={44} sx={{ borderRadius: 1 }} />
              <Box sx={{ flexGrow: 1 }}>
                <Skeleton variant="text" width="70%" />
                <Skeleton variant="text" width="45%" />
              </Box>
            </Stack>
          ))}
        </Stack>
      ) : (
        <Stack sx={{ gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            עדיין לא שותפו נכסים עם הלקוח — בחרו נכסים מתאימים בטאב ההתאמות.
          </Typography>
          <Button
            size="small"
            variant="text"
            onClick={() => switchTab("matches")}
            sx={{ alignSelf: "flex-start" }}
          >
            לניהול הנכסים ←
          </Button>
        </Stack>
      )}
    </OverviewPanel>
  );
}
